import { useAppSelector } from 'app/hooks';
import type { IDashboardUser, BoardPermission } from 'models';
import LeftResizer from './LeftResizer';
import StatusesRail from './StatusesRail';
import ContainerHeader from './ContainerHeader';
import NoRolePanel from './NoRolePanel';
import ProjectTable from './ProjectTable';
import DashboardModals from './DashboardModals';

/**
 * #ksdm-content — the dashboard app shell (main.tmpl + container.tmpl chrome),
 * §2. Role-gated: when the user has no role_id, only the NoRolePanel renders
 * (no rail, no table). The no-role header/footer class changes are applied by
 * DashboardPage (it owns #ksdm-header and the footer).
 *
 * SUB-PHASE 2a SCOPE: the full container chrome (container-header, content card,
 * preloaders, admin-table heading/rows shell, infobar placeholder) renders, but
 * the project table BODY, the column headers, the modals' internals, and realtime
 * are deferred. The empty <ul id="list_heading_noname">, the two empty
 * <ul.list-rows> and the empty #infobar / #new_project_popup / #settings are the
 * intentional mount points where 2b/2c attach.
 */
interface DashboardShellProps {
  user: IDashboardUser | null;
  permission: BoardPermission;
}

const DashboardShell = ({ user, permission }: DashboardShellProps) => {
  const collapsed = useAppSelector((s) => s.dashboard.collapsed);
  // process preloader is active by default on initial load (parity item 174);
  // its lifecycle is finalized once the table renders in 2b.
  // No-role only once the user has LOADED without a role_id — while the user
  // query is still in flight (user === null) render the normal shell (its
  // preloader is active by default), never the NoRolePanel (QA: the message
  // flashed during initial load).
  const noRole = !!user && !user.role_id;

  if (noRole) {
    return (
      <div id="ksdm-content">
        <NoRolePanel />
      </div>
    );
  }

  const addDisabled = permission === 'view' || permission === 'no_access';

  return (
    <div id="ksdm-content">
      <LeftResizer />

      <div id="statuses" className="col-xs-2">
        <StatusesRail />
      </div>

      <div id="container" className="col-xs-10">
        <ContainerHeader addDisabled={addDisabled} />

        <div className="content content-with-pagers">
          <div className="content-item">
            <div className="whiteboard-table first-fixed-table-content">
              <div id="whiteboard_preloader" className="preloader dotted-preloader" />
              <div id="dotted_preloader" className="preloader dotted-preloader row-loader" />
              <div
                id="process_dotted_preloader"
                className="preloader dotted-preloader active"
              />
              {/* ProjectTable owns the .admin-table scroll container, the
                  #list_heading + #list_heading_noname header, the pinned/default
                  row lists, and the #infobar (2b). */}
              <ProjectTable permission={permission} />
              {/* NewProjectModal portals into this mount when opened (2c). */}
              <div id="new_project_popup" />
            </div>
          </div>
        </div>
      </div>

      {/* ColumnSettings modal portals into this mount when opened (2c). */}
      <div id="settings" />

      {/* Stand-alone modals (New Project / Manage Columns / No Permission);
          each renders only while its dashboardSlice flag is set. */}
      <DashboardModals />

      {/* `collapsed` is consumed by the CSS sibling rules on #left_resizer;
          referenced here so the layout effect re-runs when it changes. */}
      <span hidden data-collapsed={collapsed ? 'true' : 'false'} />
    </div>
  );
};

export default DashboardShell;
